/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { useSyncExternalStore } from 'react'
import type { Box, MailState } from '../../../types/state'
import { MailBoxType } from '../../constants/enums'
import { handleMailResponse } from '../../mfcLogic/api/mail'
import { MAILSTATE } from '../../state'
import { pubSubMail } from '../../state/pubSub'
import {
    _fetchMailBody,
    _markMailMessage,
    _postMail,
    checkMail,
} from '../../state/state.mail'
import { loadWithRetry } from '../../utilities/dynamicWithRetry'
import useInterval from './useInterval'
import { usePassive } from './usePassive'

const MAIL_CHECK_INTERVAL = 60000

interface openMessageOptions {
    mailId?: number
    box?: Box
    loadBody?: boolean
    clear?: boolean
}
export const openMailMessage = (options: openMessageOptions) => {
    const { mailId, box, loadBody = true, clear = false } = options
    // 1) set / clear the mailId and box in opened
    pubSubMail.emit('*mail_opened', { mailId, box }, clear)

    // 2) possibly trigger _fetchMailBody
    // a) if we dont have the parsedBody already trigger a load
    const found = MAILSTATE.mail.messages?.[box]?.find(
        (message) => message.id === mailId,
    )
    if (loadBody && mailId && !found?.parsedBody) {
        if (found?.error) {
            pubSubMail.emit('*mail_body', { box, mailId, error: false })
        }
        loadWithRetry(() => _fetchMailBody(mailId))
            .then(([body, parsedBody]) => {
                // 3) emit the body results
                pubSubMail.emit('*mail_body', { parsedBody, body, box, mailId })
            })
            .catch((error: unknown) => {
                if ((error as Error)?.name === 'AbortError') return
                pubSubMail.emit('*mail_body', { box, mailId, error: true })
            })
    }
}

/// need to ask for the token for now since we dont have it in our
export const starMessage = (
    mailIds: number[],
    box: Box,
    starred: boolean,
    token: string,
) => {
    const starValue = starred ? 1 : 0
    _markMailMessage(mailIds, 'star', starValue, token).then(({ success }) => {
        if (!success) {
            // handle errors somehow?
            // COMMON.actions.openError({ message: response })
            // console.error('_markMailMessage failed', {
            //     mailIds,
            //     success,
            //     type: 'star',
            // })
        }
        pubSubMail.emit('*mail_mark', {
            mailIds,
            box,
            key: 'star',
            value: starValue,
        })
    })
}

export const readMessage = (
    mailIds: number[],
    box: Box,
    read: boolean,
    token: string,
) => {
    const readValue = read ? 1 : 0

    _markMailMessage(mailIds, 'read', readValue, token).then(({ success }) => {
        if (!success) {
            // handle errors somehow?
            // COMMON.actions.openError({ message: response })
            // console.error('_markMailMessage failed', {
            //     mailIds,
            //     success,
            //     type: 'read',
            // })
        }
        pubSubMail.emit('*mail_mark', {
            mailIds,
            box,
            key: 'message_read',
            value: readValue,
        })
    })
}

export const deleteMessage = (mailIds: number[], box: Box, token: string) => {
    const deleteValue = box === MailBoxType.inbox ? 'delete' : 'delete_sent'

    _markMailMessage(mailIds, deleteValue, 1, token).then(({ success }) => {
        if (!success) {
            // handle errors somehow?
            // COMMON.actions.openError({ message: response })
            // console.error('_markMailMessage failed', {
            //     mailIds,
            //     success,
            //     type: deleteValue,
            // })
        }
        pubSubMail.emit('*mail_mark', {
            mailIds,
            box,
            key: 'delete',
            value: 1,
        })
    })
}

type SendMessageOptions = {
    to?: string
    subject?: string
    message?: string
    token?: string
    autoCheck?: boolean
}

export const sendMessage = async (options: SendMessageOptions) => {
    const {
        to = '',
        subject = '',
        message = '',
        token,
        autoCheck = true,
    } = options
    const postMailResponse = await _postMail(to, subject, message, token)
    if (autoCheck) {
        checkMail(MailBoxType.sent)
    }
    const result = handleMailResponse(postMailResponse)
    return result
}
const subMailState = (callback: () => void) => pubSubMail.on('mail', callback)

const getMailState = () => MAILSTATE.mail

const getMailStateSelector =
    <T extends keyof MailState['mail']>(key: T) =>
    () =>
        MAILSTATE.mail[key]

interface UseMailOptions {
    haltInterval?: boolean
}

export const useMail = ({ haltInterval }: UseMailOptions = {}) => {
    const passiveMode = usePassive()
    const mail = useSyncExternalStore(subMailState, getMailState, getMailState)
    const {
        messages: { inbox = [], sent = [] },
    } = mail

    // check mail every `MAIL_CHECK_INTERVAL` ms while the mail page is open
    useInterval(
        'mail',
        checkMail,
        MAIL_CHECK_INTERVAL,
        haltInterval || passiveMode,
    )

    /// how should errors be propagated back?
    return {
        messages: { inbox, sent },
        opened: { mailId: mail.opened.mailId, box: mail.opened.box },
        loading: mail.loading,
        unread: mail.unread,
    }
}

export const useMailStateSelector = <T extends keyof MailState['mail']>(
    key: T,
): MailState['mail'][T] =>
    useSyncExternalStore(
        subMailState,
        getMailStateSelector(key),
        getMailStateSelector(key),
    )
